import type { NextConfig } from "next";
import { getCdnUrl } from "../utils/cdn";

/** Trex defaults plus any `NextConfig` fields; unknown keys are applied last and override presets. */
export type Trex2bNextConfigOptions = NextConfig & {
  /** Monorepo root (same as previous `path.join(__dirname, "../..")` from each app). */
  workspaceRoot: string;
  /** Extra packages to transpile (e.g. `recharts` for Dashboard). */
  transpilePackages?: string[];
  /** Emit browser source maps in production (e.g. for Bugsnag upload). */
  productionBrowserSourceMaps?: boolean;
};

/**
 * Shared Next.js config: monorepo `turbopack.root`, optional CDN `assetPrefix` + custom image loader.
 * CDN values come from `@trex-2b/libs/cdn` (`NEXT_PUBLIC_CDN_URL` + `NEXT_PUBLIC_CDN_STATIC_PREFIX`).
 *
 * Pass any other `NextConfig` option on the same object; they are spread after the Trex defaults so
 * you can override (e.g. `rewrites`, `images`, `assetPrefix`). `workspaceRoot` is consumed for
 * `turbopack.root` only and is not forwarded to Next.
 */
export function createTrex2bNextConfig(
  options: Trex2bNextConfigOptions,
): NextConfig {
  const { workspaceRoot, transpilePackages, ...overrides } = options;
  const CDN_URL = getCdnUrl();
  const shouldUseCdn = process.env.NODE_ENV === "production" && !!CDN_URL;
  const cdnRemotePattern = (() => {
    if (!CDN_URL) return undefined;
    try {
      const parsed = new URL(CDN_URL);
      return {
        protocol: parsed.protocol.replace(":", "") as "http" | "https",
        hostname: parsed.hostname,
        port: parsed.port || undefined,
        pathname: "/**",
      };
    } catch {
      return undefined;
    }
  })();

  return {
    ...(options.productionBrowserSourceMaps !== undefined
      ? { productionBrowserSourceMaps: options.productionBrowserSourceMaps }
      : {}),
    ...(options.transpilePackages?.length
      ? { transpilePackages: options.transpilePackages }
      : {}),
    ...(process.env.DOCKER_BUILD === "true"
      ? {
          output: "standalone",
          outputFileTracingRoot: workspaceRoot,
        }
      : {}),
    turbopack: {
      root: options.workspaceRoot,
    },
    assetPrefix: shouldUseCdn ? CDN_URL : undefined,
    images: shouldUseCdn
      ? {
          loader: "custom",
          loaderFile: "./src/lib/image-loader.ts",
          remotePatterns: cdnRemotePattern ? [cdnRemotePattern] : undefined,
        }
      : {
          remotePatterns: cdnRemotePattern ? [cdnRemotePattern] : undefined,
        },
    ...overrides,
  };
}
